import React, { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag } from "antd";
import { toast } from "react-toastify";
import {
  GroupDto,
  RoleDto,
  listGroups,
  listRoles,
  createGroup,
  updateGroup,
  deleteGroup,
} from "../../../services/adminService";

export default function GroupsAdmin() {
  const [groups, setGroups] = useState<GroupDto[]>([]);
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<GroupDto | null>(null);
  const [adding, setAdding] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const [g, r] = await Promise.all([listGroups(), listRoles()]);
      setGroups(g);
      setRoles(r);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Failed to load groups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSubmit = async (values: { name: string; description?: string; role_ids?: string[] }) => {
    try {
      if (editing) {
        await updateGroup(editing.id, {
          description: values.description,
          role_ids: values.role_ids ?? [],
        });
        toast.success("Group updated");
      } else {
        await createGroup({
          name: values.name,
          description: values.description,
          role_ids: values.role_ids ?? [],
        });
        toast.success("Group created");
      }
      setEditing(null);
      setAdding(false);
      form.resetFields();
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const onDelete = async (id: string) => {
    try {
      await deleteGroup(id);
      toast.success("Group deleted");
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const roleOptions = roles.map((r) => ({ label: r.name, value: r.id }));

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Description", dataIndex: "description", key: "description" },
    {
      title: "Roles",
      key: "roles",
      render: (_: any, g: GroupDto) => (
        <>
          {g.roles.map((r) => (
            <Tag key={r.id}>{r.name}</Tag>
          ))}
        </>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, g: GroupDto) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setEditing(g);
              form.setFieldsValue({
                name: g.name,
                description: g.description,
                role_ids: g.roles.map((r) => r.id),
              });
            }}
          >
            Edit
          </Button>
          <Popconfirm title="Delete this group?" onConfirm={() => onDelete(g.id)}>
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Button
          type="primary"
          onClick={() => {
            setAdding(true);
            form.resetFields();
          }}
        >
          Add Group
        </Button>
      </div>
      <Table rowKey="id" loading={loading} dataSource={groups} columns={columns} pagination={false} />

      <Modal
        title={editing ? "Edit Group" : "Add Group"}
        open={adding || editing !== null}
        onCancel={() => {
          setEditing(null);
          setAdding(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Group name is required" }]}
          >
            <Input disabled={!!editing} />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label="Roles granted by this group" name="role_ids">
            <Select mode="multiple" options={roleOptions} optionFilterProp="label" allowClear />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
